import { DetailsTravelItem } from '@/app/models/detailsTravel';
import { FinancialGroup } from '@/app/models/financalGroup';
import { PersonResponse } from '@/app/models/person';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchDetailsTravelQuery, useUnitTravelMutation } from '@/app/services/detailsTravelApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import { useSearchPersonQuery } from '@/app/services/personApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import NewEditTravelDetails from './NewEditTravelDetails';
import TavelTable from './TavelTable';

interface TravelProps {
  group: FinancialGroup;
}

function Travel(props: TravelProps) {
  const theme = useTheme();

  const { group } = props;
  const { code } = useParams();
  const period = usePeriod();

  const { t } = useTranslation(['travel', 'common']);

  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();
  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [originPerson, setOriginPerson] = useState<PersonResponse | null>(null);
  const [originPersonSearch, setOriginPersonSearch] = useState('');
  const { data: originPersons, isFetching: isLoadingOrigin } = useSearchPersonQuery(
    {
      searchTerm: originPersonSearch,
      page: 0,
      size: 10,
      sortKey: '',
    },
    {
      skip: originPersonSearch.length < 1,
    },
  );

  const { data: travel } = useSearchDetailsTravelQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originPerson?.personBadge || '',
    },
    {
      skip: !period.selectedPeriod?.date || !code || !selectedFinancialSubGroup,
    },
  );

  const [selectTravel, setSelectTravel] = useState<number[]>([]);
  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);

  const [selectTravelItem, setSelectedTravelItem] = useState<DetailsTravelItem>();

  const [unit] = useUnitTravelMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectTravel,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectTravel([]);
  };
  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();
  const { selectedFinancialGroup } = useFinancialGroup();
  useEffect(() => {
    if (!selectTravelItem || !selectedFinancialSubGroup || !period.selectedPeriod?.date) return;

    setSelectedItem({
      id: selectTravelItem?.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: period.selectedPeriod.date,
      complement: selectTravelItem.complement,
      newDetail: {
        description: selectTravelItem?.person?.name,
        value: selectTravelItem.value,
      },
    });
  }, [selectedFinancialSubGroup, selectedFinancialGroup?.code, selectTravelItem, period.selectedPeriod]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originPerson}
              onChange={(value) => {
                setOriginPerson(value);
              }}
              equalityTest={(option, value) => option?.personBadge === value?.personBadge}
              getOptionLabel={(option) => (option ? `${option.personBadge ?? ''} - ${option.name ?? ''}` : '')}
              onSearch={(value) => {
                setOriginPersonSearch(value);
              }}
              options={originPersonSearch !== '' && originPersons?.content ? originPersons.content : []}
              placeholder={t('common:type.to.search') as string}
              loading={isLoadingOrigin}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(travel?.totalValue || null)}</Typography>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectTravel.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <TavelTable
        selectTravel={selectTravel}
        setSelectTravel={setSelectTravel}
        setDrawerEdit={setDrawerEdit}
        setSelectedTravelItem={setSelectedTravelItem}
        selectTravelItem={selectTravelItem as DetailsTravelItem}
        setDrawerTransfer={setDrawerTransfer}
        equipmentData={travel}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditTravelDetails selectItem={selectTravelItem as DetailsTravelItem} setDrawerOpen={setDrawerEdit} />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Travel;
